import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import {
  Loader2, Save, X, Pencil, MapPin, Clock, DollarSign, User,
  FileText, Truck, Building2, AlertTriangle, CheckCircle2, Info,
  RotateCcw, ChevronDown, ChevronRight, Lock,
} from 'lucide-react';
import type { ExportJobRow } from './types';

// ─── Props ──────────────────────────────────────────────────────────

interface JobEditModalProps {
  open: boolean;
  onClose: () => void;
  row: ExportJobRow | null;
  /** Called after a successful save so the parent can refresh data */
  onSaved?: (updatedRow: ExportJobRow) => void;
}

// ─── Field Definitions ──────────────────────────────────────────────

interface FieldDef {
  key: string;
  label: string;
  /** The actual DB column name (may differ from the view alias) */
  dbColumn: string;
  type: 'text' | 'number' | 'date' | 'time' | 'textarea' | 'boolean' | 'select';
  table: 'taxicaller' | 'payment' | 'crm';
  readOnly?: boolean;
  selectOptions?: { value: string; label: string }[];
  hint?: string;
  width?: 'full' | 'half';
}

const TC_FIELDS: FieldDef[] = [
  { key: 'job_id', label: 'Job ID', dbColumn: 'Job_ID', type: 'text', table: 'taxicaller', readOnly: true },
  { key: 'project', label: 'Project', dbColumn: 'Project', type: 'text', table: 'taxicaller', width: 'half' },
  { key: 'account_id', label: 'Account ID', dbColumn: 'Account_ID', type: 'text', table: 'taxicaller', width: 'half' },
  { key: 'reference', label: 'Reference', dbColumn: 'Reference', type: 'text', table: 'taxicaller' },
  { key: 'passenger_first_name', label: 'Passenger First Name', dbColumn: 'Passenger_First_Name', type: 'text', table: 'taxicaller', width: 'half' },
  { key: 'passenger_last_name', label: 'Passenger Last Name', dbColumn: 'Passenger_Last_Name', type: 'text', table: 'taxicaller', width: 'half' },
  { key: 'pick_up_location', label: 'Pickup Location', dbColumn: 'Pick_Up_Location', type: 'text', table: 'taxicaller' },
  { key: 'drop_off_location', label: 'Dropoff Location', dbColumn: 'Drop_Off_Location', type: 'text', table: 'taxicaller' },
  { key: 'pu_date', label: 'Pickup Date', dbColumn: 'pu_date', type: 'date', table: 'taxicaller', width: 'half' },
  { key: 'pu_time', label: 'Pickup Time', dbColumn: 'pu_time', type: 'time', table: 'taxicaller', width: 'half' },
  { key: 'do_time', label: 'Dropoff Time', dbColumn: 'do_time', type: 'time', table: 'taxicaller', width: 'half' },
  { key: 'tc_vehicle_number', label: 'Vehicle Number', dbColumn: 'vehicle_number', type: 'text', table: 'taxicaller', width: 'half' },
  { key: 'tc_driver_name', label: 'Driver Name', dbColumn: 'driver_name', type: 'text', table: 'taxicaller', width: 'half' },
  { key: 'tc_cost_code', label: 'Cost Code', dbColumn: 'Cost_Code', type: 'text', table: 'taxicaller', width: 'half' },
  { key: 'stage', label: 'Stage', dbColumn: 'Stage', type: 'select', table: 'taxicaller', width: 'half', selectOptions: [
    { value: 'Delivered', label: 'Delivered' },
    { value: 'Cancelled', label: 'Cancelled' },
    { value: 'No show', label: 'No Show' },
    { value: 'Passenger on board', label: 'Passenger on Board' },
    { value: 'Assigned', label: 'Assigned' },
  ]},
  { key: 'fleet', label: 'Fleet', dbColumn: 'fleet', type: 'text', table: 'taxicaller', width: 'half' },
  { key: 'grand_total', label: 'Grand Total', dbColumn: 'grand_total', type: 'number', table: 'taxicaller', width: 'half' },
  { key: 'trip_total', label: 'Trip Total', dbColumn: 'trip_total', type: 'number', table: 'taxicaller', width: 'half' },
  { key: 'trip_base_fare', label: 'Trip Base Fare', dbColumn: 'trip_base_fare', type: 'number', table: 'taxicaller', width: 'half' },
  { key: 'direct_settled', label: 'Direct Settled', dbColumn: 'direct_settled', type: 'number', table: 'taxicaller', width: 'half' },
  { key: 'job_km', label: 'Job KM', dbColumn: 'job_km', type: 'number', table: 'taxicaller', width: 'half' },
];

const PAYMENT_FIELDS: FieldDef[] = [
  { key: 'paid_amount', label: 'Paid Amount', dbColumn: 'paid_amount', type: 'number', table: 'payment', width: 'half' },
  { key: 'cash_amt', label: 'Cash', dbColumn: 'cash_amt', type: 'number', table: 'payment', width: 'half' },
  { key: 'eftpos_amt', label: 'EFTPOS', dbColumn: 'eftpos_amt', type: 'number', table: 'payment', width: 'half' },
  { key: 'totalmobility_amt', label: 'Total Mobility', dbColumn: 'totalmobility_amt', type: 'number', table: 'payment', width: 'half' },
  { key: 'tm_hoist_amt', label: 'TM Hoist', dbColumn: 'tm_hoist_amt', type: 'number', table: 'payment', width: 'half' },
  { key: 'account_amt', label: 'Account Amount', dbColumn: 'account_amt', type: 'number', table: 'payment', width: 'half' },
  { key: 'other_amt', label: 'Other Amount', dbColumn: 'other_amt', type: 'number', table: 'payment', width: 'half' },
  { key: 'discount', label: 'Discount', dbColumn: 'discount', type: 'number', table: 'payment', width: 'half' },
  { key: 'ecard_amt', label: 'eCard Amount', dbColumn: 'ecard_amt', type: 'number', table: 'payment', width: 'half' },
  { key: 'eftpos_valid', label: 'EFTPOS Valid', dbColumn: 'eftpos_valid', type: 'boolean', table: 'payment', width: 'half' },
  { key: 'total_mobility_valid', label: 'TM Valid', dbColumn: 'total_mobility_valid', type: 'boolean', table: 'payment', width: 'half' },
  { key: 'tm_hoist_valid', label: 'TM Hoist Valid', dbColumn: 'tm_hoist_valid', type: 'boolean', table: 'payment', width: 'half' },
  { key: 'ecard_valid', label: 'eCard Valid', dbColumn: 'ecard_valid', type: 'boolean', table: 'payment', width: 'half' },
  { key: 'reconciled', label: 'Reconciled', dbColumn: 'reconciled', type: 'boolean', table: 'payment', width: 'half' },
  { key: 'driver_paid', label: 'Driver Paid', dbColumn: 'driver_paid', type: 'boolean', table: 'payment', width: 'half' },
  { key: 'payment_notes', label: 'Payment Notes', dbColumn: 'notes', type: 'textarea', table: 'payment' },
];

const CRM_FIELDS: FieldDef[] = [
  { key: 'account_name', label: 'Account Name', dbColumn: 'account_name', type: 'text', table: 'crm', readOnly: true, width: 'half' },
  { key: 'account_type', label: 'Account Type', dbColumn: 'account_type', type: 'text', table: 'crm', readOnly: true, width: 'half' },
  { key: 'billed_status', label: 'Billed Status', dbColumn: 'billed_status', type: 'text', table: 'crm', readOnly: true, width: 'half' },
  { key: 'billing_name', label: 'Billing Name', dbColumn: 'billing_name', type: 'text', table: 'crm', readOnly: true, width: 'half' },
  { key: 'invoice_email', label: 'Invoice Email', dbColumn: 'invoice_email', type: 'text', table: 'crm', readOnly: true, width: 'half' },
  { key: 'payment_terms', label: 'Payment Terms', dbColumn: 'payment_terms', type: 'text', table: 'crm', readOnly: true, width: 'half' },
  { key: 'account_category', label: 'Account Category', dbColumn: 'account_category', type: 'text', table: 'crm', readOnly: true, width: 'half' },
  { key: 'account_series_type', label: 'Series Type', dbColumn: 'account_series_type', type: 'text', table: 'crm', readOnly: true, width: 'half' },
];

// ─── Component ──────────────────────────────────────────────────────

const JobEditModal: React.FC<JobEditModalProps> = ({ open, onClose, row, onSaved }) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [originalData, setOriginalData] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    taxicaller: true,
    payment: true,
    crm: false,
  });

  // Initialize form data when row changes
  useEffect(() => {
    if (row) {
      const data: Record<string, any> = {};
      [...TC_FIELDS, ...PAYMENT_FIELDS, ...CRM_FIELDS].forEach(f => {
        const val = (row as any)[f.key];
        data[f.key] = val ?? (f.type === 'number' ? '' : f.type === 'boolean' ? false : '');
      });
      setFormData(data);
      setOriginalData({ ...data });
    }
  }, [row]);

  const handleChange = useCallback((key: string, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const hasChanges = useCallback(() => {
    return Object.keys(formData).some(key => {
      const orig = originalData[key];
      const curr = formData[key];
      // Handle number comparison
      if (typeof orig === 'number' || typeof curr === 'number') {
        return Number(orig || 0) !== Number(curr || 0);
      }
      // Handle boolean comparison
      if (typeof orig === 'boolean' || typeof curr === 'boolean') {
        return Boolean(orig) !== Boolean(curr);
      }
      // Handle null/empty string comparison
      return String(orig ?? '') !== String(curr ?? '');
    });
  }, [formData, originalData]);

  const getChangedFields = useCallback((table: 'taxicaller' | 'payment') => {
    const fields = table === 'taxicaller' ? TC_FIELDS : PAYMENT_FIELDS;
    const changed: Record<string, any> = {};
    fields.forEach(f => {
      if (f.readOnly) return;
      const orig = originalData[f.key];
      const curr = formData[f.key];
      let isChanged = false;
      if (f.type === 'number') {
        isChanged = Number(orig || 0) !== Number(curr || 0);
      } else if (f.type === 'boolean') {
        isChanged = Boolean(orig) !== Boolean(curr);
      } else {
        isChanged = String(orig ?? '') !== String(curr ?? '');
      }
      if (isChanged) {
        let val = curr;
        if (f.type === 'number') {
          val = curr === '' || curr === null ? null : Number(curr);
        } else if (f.type === 'boolean') {
          val = Boolean(curr);
        } else if (f.type === 'text' || f.type === 'textarea') {
          val = curr === '' ? null : String(curr);
        }
        changed[f.dbColumn] = val;
      }
    });
    return changed;
  }, [formData, originalData]);

  const handleSave = useCallback(async () => {
    if (!row) return;
    setSaving(true);

    try {
      const tcChanges = getChangedFields('taxicaller');
      const payChanges = getChangedFields('payment');
      const promises: Promise<any>[] = [];
      const changeLog: string[] = [];

      // Update taxicaller_data
      if (Object.keys(tcChanges).length > 0) {
        changeLog.push(`TaxiCaller: ${Object.keys(tcChanges).join(', ')}`);
        promises.push(
          supabase
            .from('taxicaller_data')
            .update(tcChanges)
            .eq('id', row.tc_id)
            .then(({ error }) => {
              if (error) throw new Error(`TaxiCaller update failed: ${error.message}`);
            })
        );
      }

      // Update driver_completed_payments
      if (Object.keys(payChanges).length > 0 && row.dcp_id) {
        changeLog.push(`Payments: ${Object.keys(payChanges).join(', ')}`);
        promises.push(
          supabase
            .from('driver_completed_payments')
            .update(payChanges)
            .eq('id', row.dcp_id)
            .then(({ error }) => {
              if (error) throw new Error(`Payment update failed: ${error.message}`);
            })
        );
      } else if (Object.keys(payChanges).length > 0 && !row.dcp_id) {
        toast({
          title: 'Cannot Update Payments',
          description: 'No payment record exists for this job. Payment fields cannot be updated.',
          variant: 'destructive',
        });
        setSaving(false);
        return;
      }

      if (promises.length === 0) {
        toast({ title: 'No Changes', description: 'No fields were modified.' });
        setSaving(false);
        return;
      }

      await Promise.all(promises);

      // Build updated row for callback
      const updatedRow: ExportJobRow = { ...row };
      [...TC_FIELDS, ...PAYMENT_FIELDS].forEach(f => {
        if (!f.readOnly) {
          (updatedRow as any)[f.key] = formData[f.key];
        }
      });

      toast({
        title: 'Job Updated',
        description: `Saved changes to ${changeLog.join(' & ')}`,
      });

      setOriginalData({ ...formData });
      onSaved?.(updatedRow);
    } catch (err: any) {
      console.error('Save error:', err);
      toast({
        title: 'Save Failed',
        description: err.message || 'An error occurred while saving. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }, [row, formData, getChangedFields, onSaved]);

  const handleReset = useCallback(() => {
    setFormData({ ...originalData });
  }, [originalData]);

  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const isFieldChanged = useCallback((key: string, type: string) => {
    const orig = originalData[key];
    const curr = formData[key];
    if (type === 'number') return Number(orig || 0) !== Number(curr || 0);
    if (type === 'boolean') return Boolean(orig) !== Boolean(curr);
    return String(orig ?? '') !== String(curr ?? '');
  }, [formData, originalData]);

  // Count changes per section
  const tcChangeCount = TC_FIELDS.filter(f => !f.readOnly && isFieldChanged(f.key, f.type)).length;
  const payChangeCount = PAYMENT_FIELDS.filter(f => !f.readOnly && isFieldChanged(f.key, f.type)).length;
  const totalChanges = tcChangeCount + payChangeCount;

  if (!row) return null;

  // ─── Render a single field ────────────────────────────────────────
  const renderField = (field: FieldDef) => {
    const value = formData[field.key];
    const changed = !field.readOnly && isFieldChanged(field.key, field.type);
    const isReadOnly = field.readOnly || (field.table === 'payment' && !row.dcp_id);

    return (
      <div
        key={field.key}
        className={`${field.width === 'half' ? 'col-span-1' : 'col-span-2'}`}
      >
        <div className="flex items-center gap-1.5 mb-1">
          <Label className={`text-[10px] uppercase tracking-wider ${
            changed ? 'text-amber-400 font-semibold' : 'text-muted-foreground'
          }`}>
            {field.label}
          </Label>
          {changed && (
            <span className="text-[8px] px-1 py-0 rounded bg-amber-900/30 text-amber-400 border border-amber-700/30">
              modified
            </span>
          )}
          {isReadOnly && (
            <Lock className="w-2.5 h-2.5 text-muted-foreground/40" />
          )}
        </div>

        {field.type === 'boolean' ? (
          <div className="flex items-center gap-2 h-8">
            <Switch
              checked={Boolean(value)}
              onCheckedChange={(v) => handleChange(field.key, v)}
              disabled={isReadOnly}
            />
            <span className={`text-xs ${value ? 'text-green-400' : 'text-muted-foreground/60'}`}>
              {value ? 'Yes' : 'No'}
            </span>
          </div>
        ) : field.type === 'select' ? (
          <Select
            value={String(value || '')}
            onValueChange={(v) => handleChange(field.key, v)}
            disabled={isReadOnly}
          >
            <SelectTrigger className={`dark-input text-xs h-8 ${changed ? 'border-amber-700/50 bg-amber-900/10' : ''} ${isReadOnly ? 'opacity-60' : ''}`}>
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {field.selectOptions?.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : field.type === 'textarea' ? (
          <Textarea
            value={String(value ?? '')}
            onChange={(e) => handleChange(field.key, e.target.value)}
            readOnly={isReadOnly}
            className={`dark-input text-xs min-h-[60px] resize-y ${changed ? 'border-amber-700/50 bg-amber-900/10' : ''} ${isReadOnly ? 'opacity-60' : ''}`}
            rows={2}
          />
        ) : (
          <Input
            type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'time' ? 'time' : 'text'}
            value={field.type === 'number' ? (value === null || value === undefined ? '' : value) : String(value ?? '')}
            onChange={(e) => handleChange(field.key, field.type === 'number' ? e.target.value : e.target.value)}
            readOnly={isReadOnly}
            step={field.type === 'number' ? '0.01' : undefined}
            className={`dark-input text-xs h-8 ${changed ? 'border-amber-700/50 bg-amber-900/10' : ''} ${isReadOnly ? 'opacity-60' : ''}`}
          />
        )}
      </div>
    );
  };

  // ─── Section renderer ─────────────────────────────────────────────
  const renderSection = (
    id: string,
    title: string,
    icon: React.ReactNode,
    fields: FieldDef[],
    color: string,
    changeCount: number,
    readOnlyNote?: string,
  ) => {
    const isExpanded = expandedSections[id];
    return (
      <div className={`border rounded-lg overflow-hidden ${color}`}>
        <button
          onClick={() => toggleSection(id)}
          className="w-full flex items-center justify-between px-3 py-2 hover:bg-secondary/20 transition-colors"
        >
          <div className="flex items-center gap-2">
            {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            {icon}
            <span className="text-xs font-semibold">{title}</span>
            <span className="text-[10px] text-muted-foreground">({fields.length} fields)</span>
          </div>
          <div className="flex items-center gap-2">
            {changeCount > 0 && (
              <Badge className="bg-amber-900/40 text-amber-400 border-amber-700/50 text-[9px] px-1.5 py-0">
                {changeCount} changed
              </Badge>
            )}
            {readOnlyNote && (
              <Badge className="bg-gray-800/40 text-gray-400 border-gray-700/50 text-[9px] px-1.5 py-0">
                {readOnlyNote}
              </Badge>
            )}
          </div>
        </button>
        {isExpanded && (
          <div className="px-3 pb-3 pt-1 border-t border-border/30">
            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              {fields.map(f => renderField(f))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[720px] max-h-[90vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-5 py-3 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Pencil className="w-4 h-4 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-sm font-semibold">Edit Job</DialogTitle>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge className="bg-blue-900/30 text-blue-400 border-blue-700/40 text-[10px] px-1.5 py-0 font-mono">
                    {row.job_id}
                  </Badge>
                  {row.account_id && (
                    <Badge className="bg-emerald-900/30 text-emerald-400 border-emerald-700/40 text-[10px] px-1.5 py-0">
                      Acct {row.account_id}
                    </Badge>
                  )}
                  {row.pu_date && (
                    <span className="text-[10px] text-muted-foreground">{row.pu_date}</span>
                  )}
                  {row.passenger_name && (
                    <span className="text-[10px] text-muted-foreground">{row.passenger_name}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {totalChanges > 0 && (
                <Badge className="bg-amber-900/40 text-amber-400 border-amber-700/50 text-[10px] px-2 py-0.5">
                  {totalChanges} unsaved change{totalChanges !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
          <DialogDescription className="sr-only">
            Edit job details for Job ID {row.job_id}. Changes are saved to the TaxiCaller and Payment tables.
          </DialogDescription>
        </DialogHeader>

        {/* Body — Scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {/* TaxiCaller Section */}
          {renderSection(
            'taxicaller',
            'TaxiCaller Data',
            <FileText className="w-3.5 h-3.5 text-blue-400" />,
            TC_FIELDS,
            'border-blue-700/20 bg-blue-900/5',
            tcChangeCount,
          )}

          {/* Payment Section */}
          {renderSection(
            'payment',
            'Driver Completed Payments',
            <DollarSign className="w-3.5 h-3.5 text-green-400" />,
            PAYMENT_FIELDS,
            'border-green-700/20 bg-green-900/5',
            payChangeCount,
            !row.dcp_id ? 'No payment record' : undefined,
          )}

          {/* CRM Section — Read Only */}
          {renderSection(
            'crm',
            'CRM Account',
            <Building2 className="w-3.5 h-3.5 text-purple-400" />,
            CRM_FIELDS,
            'border-purple-700/20 bg-purple-900/5',
            0,
            'Read-only',
          )}

          {/* No payment record warning */}
          {!row.dcp_id && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-900/10 border border-amber-700/20">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-400/90">
                <p className="font-semibold">No Payment Record</p>
                <p className="text-amber-400/70 mt-0.5">
                  This job does not have a matching driver completed payment record. Payment fields cannot be edited.
                </p>
              </div>
            </div>
          )}

          {/* CRM read-only note */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-secondary/30 border border-border/30">
            <Info className="w-4 h-4 text-muted-foreground/60 flex-shrink-0 mt-0.5" />
            <div className="text-[11px] text-muted-foreground/80">
              <p>CRM Account fields are <strong>read-only</strong> here because changes would affect all jobs for this account. To edit CRM data, use the CRM Accounts page.</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="px-5 py-3 border-t border-border flex-shrink-0">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              {totalChanges > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleReset}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  Reset
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={onClose}
                className="text-xs text-muted-foreground"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving || totalChanges === 0}
                className="golden-button text-xs"
              >
                {saving ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5 mr-1" />
                )}
                Save Changes
                {totalChanges > 0 && ` (${totalChanges})`}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default JobEditModal;
